import React, { useEffect, useRef } from 'react';

const positiveUseCases = [
  "A way to test your product with real people",
  "A tool to get validated results like glucose charts and feedback",
  "Fast, flexible, and easy to repeat",
  "Great for marketing and learning what works",
  "More affordable than clinical trials"
];

const negativeUseCases = [
  "A clinical trial or FDA study",
  "A one-time, rigid experiment",
  "A guarantee of positive results",
  "Just another influencer campaign—we focus on real data"
];

const UseCasesSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.animate-on-scroll');
            elements.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('visible');
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="section-padding bg-neutral-50">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-12 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            What Tastermonial Is
          </h2>
          <p className="text-neutral-700 text-lg mb-8">
            Our platform helps you validate your product's impact with real-world data.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div>
            <h3 className="text-xl font-semibold mb-6 text-primary">Tastermonial is:</h3>
            <div className="space-y-4">
              {positiveUseCases.map((useCase, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg p-4 border border-neutral-200 shadow-soft animate-on-scroll flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="font-medium">{useCase}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-6 text-neutral-600">Tastermonial is not:</h3>
            <div className="space-y-4">
              {negativeUseCases.map((useCase, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg p-4 border border-neutral-200 shadow-soft animate-on-scroll flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <span className="font-medium">{useCase}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="text-center max-w-3xl mx-auto animate-on-scroll">
          <p className="text-lg font-medium text-neutral-800">
            Tastermonial helps you prove your product works in the real world, not just in theory.
          </p>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
